import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { SEOHead } from '../components';
import { ExecutiveFlowRail, ResearchApproachDiagram } from '../components/executive';
import { researchOfferings } from '../data/researchOfferings';

const approachPhases = [
  {
    title: 'Frame The Product Thesis',
    description:
      'Define the operator, the decision, the governing constraint, and the evidence that would make the premise credible.',
  },
  {
    title: 'Design The Control Model',
    description:
      'Resolve privacy boundaries, inference placement, agent authority, regulatory evidence, and human oversight before implementation makes them expensive to change.',
  },
  {
    title: 'Build And Instrument',
    description:
      'Use agents across the full plan-act-observe loop, with scoped tools, observable decisions, durable records, and controls that fail closed.',
  },
  {
    title: 'Verify In Operation',
    description:
      'Test intended paths, privacy boundaries, denied actions, and break cases. Compare observed behavior with the thesis and retain only supported conclusions.',
  },
];

const researchPrinciples = [
  {
    title: 'Privacy As A System Boundary',
    description:
      'Sensitive context is classified, minimized, and kept inside explicit access boundaries. Local inference is used when data exposure, residency, latency, or operator control makes an external model the wrong architecture.',
  },
  {
    title: 'Agentic AI With Defined Authority',
    description:
      'Agents can plan, retrieve, compare, invoke tools, observe results, and revise their next action. Permission scopes, budgets, stop states, and human decision points make that autonomy operationally accountable.',
  },
  {
    title: 'Regulatory Evidence By Design',
    description:
      'Applicable obligations are translated into control points and retained evidence. The system should help counsel, auditors, and operators reconstruct what occurred; it should never imply that software replaces their judgment.',
  },
  {
    title: 'One Method Across Domains',
    description:
      'Finance, insurance acquisition, restricted document retrieval, and regulated cultivation impose different rules. The engineering method remains stable: define the boundary, encode authority, observe behavior, and verify the record.',
  },
];

const cardAccent = {
  indigo: {
    bar: 'bg-indigo-500',
    pill: {
      dark: 'bg-indigo-500/20 text-indigo-100 border-indigo-500/40',
      light: 'bg-indigo-50 text-indigo-950 border-indigo-300',
    },
    hover: { dark: 'hover:border-indigo-500/45', light: 'hover:border-indigo-400' },
    cta: { dark: 'text-indigo-300', light: 'text-indigo-800' },
  },
  sky: {
    bar: 'bg-sky-500',
    pill: {
      dark: 'bg-sky-500/20 text-sky-100 border-sky-500/40',
      light: 'bg-sky-50 text-sky-950 border-sky-300',
    },
    hover: { dark: 'hover:border-sky-500/45', light: 'hover:border-sky-400' },
    cta: { dark: 'text-sky-300', light: 'text-sky-800' },
  },
  emerald: {
    bar: 'bg-emerald-500',
    pill: {
      dark: 'bg-emerald-500/20 text-emerald-100 border-emerald-500/40',
      light: 'bg-emerald-50 text-emerald-950 border-emerald-300',
    },
    hover: { dark: 'hover:border-emerald-500/45', light: 'hover:border-emerald-400' },
    cta: { dark: 'text-emerald-300', light: 'text-emerald-800' },
  },
  teal: {
    bar: 'bg-teal-500',
    pill: {
      dark: 'bg-teal-500/20 text-teal-100 border-teal-500/40',
      light: 'bg-teal-50 text-teal-950 border-teal-300',
    },
    hover: { dark: 'hover:border-teal-500/45', light: 'hover:border-teal-400' },
    cta: { dark: 'text-teal-300', light: 'text-teal-800' },
  },
};

const Research = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div className={`min-h-screen ${isDark ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-800'}`}>
      <SEOHead
        title="Applied Research — From Product Thesis To Verified System"
        description="Shared Oxygen plans, designs, builds, and verifies privacy-aware, agentic systems with local inference options and evidence for regulated operations across multiple domains."
        type="website"
      />

      <section
        className={`relative overflow-hidden ${
          isDark ? 'bg-slate-950' : 'bg-gradient-to-b from-white to-slate-50'
        } py-24 lg:py-32`}
      >
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="grid xl:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div
                className={`inline-block px-4 py-1.5 rounded-full ${
                  isDark ? 'bg-slate-800 border border-slate-700' : 'bg-blue-50 border border-blue-100'
                }`}
              >
                <span
                  className={`text-xs uppercase tracking-[0.3em] ${
                    isDark ? 'text-slate-300' : 'text-slate-700'
                  }`}
                >
                  Applied Research
                </span>
              </div>
              <h1
                className={`max-w-xl text-3xl sm:text-4xl lg:text-[2.75rem] xl:text-5xl font-semibold leading-[1.08] tracking-[-0.02em] ${
                  isDark ? 'text-white' : 'text-slate-950'
                }`}
              >
                Engineering Evidence For Decisions That Must Survive Production.
              </h1>
              <p
                className={`max-w-xl text-lg lg:text-xl leading-[1.65] ${
                  isDark ? 'text-slate-200' : 'text-slate-800'
                }`}
              >
                We take a product thesis through planning, system design, implementation, and
                verification. The result is more than a working product: it is current evidence
                about where architecture holds, where controls fail, and what operators actually
                need. Across domains, the same questions recur: where data lives, what an agent may
                do, which obligations govern the workflow, and how the resulting behavior can be
                verified.
              </p>
              <a
                href={`/Shared-Oxygen-Navigator-standalone.html?theme=${theme}`}
                className={`group inline-flex w-fit items-center gap-2 rounded-md border-2 px-7 py-3.5 text-base font-semibold transition-colors ${
                  isDark
                    ? 'border-slate-600 text-slate-100 hover:border-slate-400 hover:bg-slate-900'
                    : 'border-slate-400 text-slate-900 hover:border-slate-600 hover:bg-white'
                }`}
              >
                Explore AI Governance Navigator
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
              </a>
            </div>
            <ResearchApproachDiagram theme={theme} />
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl space-y-6">
            <p
              className={`text-xs uppercase tracking-[0.4em] font-semibold ${
                isDark ? 'text-slate-400' : 'text-slate-600'
              }`}
            >
              Why We Build
            </p>
            <h2 className={`text-3xl lg:text-4xl font-semibold ${isDark ? 'text-white' : 'text-slate-950'}`}>
              A Sound Principle Is Only The Beginning.
            </h2>
            <p className={`text-lg leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
              There is a practical difference between recognizing an architecture pattern and
              owning its consequences. A diagram will not reveal an ambiguous permission boundary,
              a control operators cannot explain, or a recovery path that fails at the worst
              moment. Those findings emerge when a product has users, state, deadlines, and
              failure conditions.
            </p>
            <p className={`text-lg leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
              Shared Oxygen builds to investigate those conditions directly. Each product isolates
              a consequential operating problem, makes its governing premise explicit, and
              produces evidence that can be inspected rather than asserted. This work is conducted
              for the decisions leaders eventually have to make: what to fund, what to control,
              what to measure, and what not to put into production.
            </p>
            <p className={`text-lg leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
              Decades spent building enterprise systems provide the longitudinal context. Current
              product work keeps that experience from becoming doctrine. Experience helps us know
              where to look; verification determines what to trust. Together, they form the
              engineering basis for our advisory.
            </p>
          </div>

          <div className="mt-14">
            <p
              className={`text-xs uppercase tracking-[0.4em] font-semibold mb-4 ${
                isDark ? 'text-slate-400' : 'text-slate-600'
              }`}
            >
              Technical Constants
            </p>
            <h3 className={`text-2xl lg:text-3xl font-semibold ${isDark ? 'text-white' : 'text-slate-950'}`}>
              The Domain Changes. The Engineering Questions Do Not.
            </h3>
            <div className="grid md:grid-cols-2 gap-6 mt-8">
              {researchPrinciples.map((principle, index) => (
                <article
                  key={principle.title}
                  className={`rounded-2xl border p-7 border-t-2 ${
                    index === 0
                      ? 'border-t-cyan-500'
                      : index === 1
                        ? 'border-t-indigo-500'
                        : index === 2
                          ? 'border-t-amber-500'
                          : 'border-t-emerald-500'
                  } ${
                    isDark
                      ? 'bg-slate-900/50 border-x-slate-800 border-b-slate-800'
                      : 'bg-white border-x-slate-200 border-b-slate-200 shadow-sm'
                  }`}
                >
                  <h4 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-950'}`}>
                    {principle.title}
                  </h4>
                  <p className={`leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    {principle.description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className={`${isDark ? 'bg-slate-900' : 'bg-slate-100'} py-20`}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <p
              className={`text-xs uppercase tracking-[0.4em] mb-4 ${
                isDark ? 'text-slate-400' : 'text-slate-600'
              }`}
            >
              Research Method
            </p>
            <h2 className={`text-3xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              From Product Thesis To Verified Behavior.
            </h2>
            <p className={`max-w-3xl mx-auto mt-4 text-lg leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
              The method is deliberately complete. Planning without construction leaves critical
              assumptions untouched. Construction without verification produces confidence, not
              evidence.
            </p>
          </div>
          <ExecutiveFlowRail phases={approachPhases} theme={theme} variant="neutral" />
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-12 max-w-3xl">
            <p
              className={`text-xs uppercase tracking-[0.4em] mb-4 font-semibold ${
                isDark ? 'text-slate-400' : 'text-slate-600'
              }`}
            >
              Product Investigations
            </p>
            <h2 className={`text-3xl lg:text-4xl font-semibold ${isDark ? 'text-white' : 'text-slate-950'}`}>
              Four Domains. One Engineering Discipline.
            </h2>
            <p className={`mt-4 text-lg leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
              Each product asks a different question about privacy, autonomous action, control,
              and trust in an operating system. Follow the links for the domain problem,
              architecture, safeguards, and current product design.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {researchOfferings.map((offering) => {
              const accent = cardAccent[offering.accent];
              const mode = isDark ? 'dark' : 'light';
              return (
                <Link
                  key={offering.path}
                  to={offering.path}
                  onClick={() => window.scrollTo(0, 0)}
                  className={`group flex text-left rounded-2xl border overflow-hidden transition-all duration-300 hover:-translate-y-1 ${
                    isDark
                      ? `bg-slate-900/50 border-slate-800 ${accent.hover.dark}`
                      : `bg-white border-slate-300 shadow-md hover:shadow-xl ${accent.hover.light}`
                  }`}
                >
                  <div className={`w-1.5 flex-shrink-0 ${accent.bar}`} aria-hidden />
                  <div className="flex flex-col justify-between flex-1 p-8">
                    <div>
                      <span
                        className={`inline-block px-2.5 py-1 rounded-full text-[11px] uppercase tracking-[0.22em] font-semibold mb-4 border ${accent.pill[mode]}`}
                      >
                        {offering.tagline}
                      </span>
                      <h3 className={`text-2xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-950'}`}>
                        {offering.name}
                      </h3>
                      <p className={`text-base leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                        {offering.summary}
                      </p>
                    </div>
                    <span
                      className={`mt-8 inline-flex items-center text-sm font-semibold ${accent.cta[mode]}`}
                    >
                      Examine The Product
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section
        className={`${
          isDark
            ? 'bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900'
            : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-slate-900'
        } py-20`}
      >
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <p className="uppercase tracking-[0.4em] text-xs mb-6 text-white/60">From Evidence To Decision</p>
          <h2 className="text-3xl lg:text-4xl font-semibold mb-6 text-white">
            Bring Us The Decision That Has To Survive Production.
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Whether you are selecting an architecture, setting operating controls, or deciding
            what to fund, we can examine the mandate against what implementation and verification
            will require.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-10 py-4 rounded-full bg-white text-slate-900 font-semibold hover:bg-slate-100 transition-colors"
            >
              Schedule a Conversation
            </Link>
            <Link
              to="/services"
              className="px-10 py-4 rounded-full border border-white/40 text-white font-semibold hover:bg-white/10 transition-colors"
            >
              Review Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Research;
